import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';
import { useHistory } from 'react-router';

import { hashMail } from '@helpers/utils';
import { showError } from '@store/common/actions';
import { setToken as setTokenAction } from '@store/login/actions';
import { validateErrorObject, succesLoginMail } from '@constants/errorObjects';
import useBreakpoint from '@hooks/breakpoint';
import useCommon from '@hooks/useCommon';

import { sendResetPasswordEmail } from '@services/login-service';

import LoginHeader from '@components/LoginHeader';
import Card from '@components/Cards/ScreenCard';
import Header from '@components/Forms/Header';
import TextAndLink from '@components/TextAndLink';
import { FORGOT_PASSWORD } from '@constants/routes';

import { getEnv } from '@config';

const { publicPath } = getEnv();

const Container = styled.div`
  white-space: pre-line;
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  ${({ theme }) => theme.breakpoint('new_md')} {
    min-height: auto;
  }
`;

const StyleCard = styled(Card)`
  div:last-child {
    display: flex;
    flex-direction: column;
    align-items: center;
  }
`;

const Text = styled.p(
  ({ theme }) => css`
    margin: 0 0 8px;
    color: ${theme.colors.gray800};
    font-family: 'Roboto';
    font-style: normal;
    font-weight: 400;
    font-size: 12px;
    line-height: 14px;
    display: flex;
    align-items: center;
    text-align: center;
  `
);

const Image = styled.img`
  align-self: center;
  width: 220px;
  margin: 40px 0 14px;
  ${({ theme }) => theme.breakpoint('new_md')} {
    margin: 6px 0 22px;
  }
`;

const EmailSentForm = () => {
  const { recover_email: recoverEmail, token: reduxStateToken } = useSelector(
    (state) => state.login
  );
  const { t } = useTranslation();
  const history = useHistory();
  const { timer, isTimeOut, getIdIntervalTimer, pushNewSentEmail, deleteTimerSentByEmail } =
    useCommon(recoverEmail);
  const dispatch = useDispatch();
  const [email, setEmail] = useState('');
  const [token, setToken] = useState('');

  const { mobile } = useBreakpoint();

  useEffect(() => {
    if (!recoverEmail || isTimeOut) return;

    const idInterval = getIdIntervalTimer();

    return () => {
      clearInterval(idInterval);
    };
  }, [recoverEmail, isTimeOut, getIdIntervalTimer]);

  useEffect(() => {
    if (!recoverEmail) return history.push(FORGOT_PASSWORD);

    setEmail(hashMail(recoverEmail));
    setToken(reduxStateToken);
    return () => {};
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const callbackError = (err) => {
    deleteTimerSentByEmail(recoverEmail);
    dispatch(showError(validateErrorObject(err)));
  };
  const callbackSucces = (rta) => {
    dispatch(showError(succesLoginMail(t('common.emailSent'))));
    dispatch(setTokenAction(rta.token));
    setToken(rta.token);
  };
  const handleResend = () => {
    pushNewSentEmail({ email: recoverEmail, token });
    sendResetPasswordEmail({ email: recoverEmail }, callbackSucces, callbackError);
  };

  return (
    <Container>
      {mobile ? <Header withoutTextMobile /> : <LoginHeader />}
      <StyleCard
        title={t('login.resetPassword.sendedEmail.title')}
        subtitle={t('login.resetPassword.sendedEmail.subTitle', {
          email,
        })}
        width="496px"
      >
        <Image src={`${publicPath}/email.svg`} alt={token || 'Imagen de email'} id="icono-email" />
        <Text>{t('login.resetPassword.sendedEmail.resendEmailQuestion')}</Text>
        <TextAndLink
          textLink={t('login.resetPassword.sendedEmail.resendEmail')}
          onClick={handleResend}
          disabled={!isTimeOut}
        >
          {t('login.resetPassword.sendedEmail.resendEmailTimer', {
            timer: timer.format('mm:ss'),
          })}
        </TextAndLink>
      </StyleCard>
    </Container>
  );
};

export default EmailSentForm;
